import { useState, useEffect } from 'react'
import { MECETable, MECETactic, WhatIfScenario, TacticInclusionState, ImplementationStatus } from '@models/index'
import { useAppDispatch } from '@state/hooks'
import { useTabNavigation } from '@contexts/TabNavigationContext'
import { navigateToGraphNode } from '@utils/crossTabNavigation'
import { getPillarNumber, getTacticNumber } from '@utils/meceNumbering'
import QuickAskModal from '@components/shared/QuickAskModal'
import { QuickAskQuestionType } from '@utils/quickAskPromptBuilder'
import { TacticInclusionToggle } from '@components/shared/TacticInclusionToggle'
import { getTacticInclusion } from '@utils/scenarioCalculator'
import { SortableTacticList } from '../editors/SortableTacticList'
import { MetricBadgeList } from '@components/shared/MetricBadge'
import { KSABadgeList } from '@components/shared/KSABadge'
import { RefDocBadgeList } from '@components/shared/RefDocBadge'
import './MECETableRenderer.css'

interface QuickAskTacticContext {
  tacticId: string
  tacticTitle: string
  tacticDescription: string
  pillarName: string
  supportingEvidence: string[]
}

interface MECETableRendererProps {
  data: MECETable | null
  onNodeClick?: (nodeId: string) => void  // Optional callback for node clicks
  onQuickAsk?: (prompt: string, questionText: string, questionType: QuickAskQuestionType, tacticContext: QuickAskTacticContext) => Promise<void>  // Phase 4.5: Quick Ask callback (async)
  // Phase 6.5: What-If Scenario props
  isScenarioMode?: boolean
  activeScenario?: WhatIfScenario | null
  onTacticInclusionChange?: (pillarIdx: number, tacticIdx: number, state: TacticInclusionState) => void
  // Phase 1.1: Pillar selection props
  isSelectionMode?: boolean
  selectedPillars?: Set<string>
  onPillarSelectionToggle?: (pillarName: string) => void
  // Phase 3.1: Priority Drag-and-Drop props
  isReorderMode?: boolean
  onTacticReorder?: (pillarIndex: number, newTactics: MECETactic[]) => void
  // Phase 3.3: Status Tracking props
  onTacticStatusChange?: (pillarIndex: number, tacticIndex: number, status: ImplementationStatus) => void
  // Phase 4: Initiative status overlay from execution tracking
  initiativeStatus?: Record<string, string>  // tactic_title -> status
}

export function MECETableRenderer({
  data,
  onNodeClick,
  onQuickAsk,
  isScenarioMode = false,
  activeScenario = null,
  onTacticInclusionChange,
  isSelectionMode = false,
  selectedPillars,
  onPillarSelectionToggle,
  isReorderMode = false,
  onTacticReorder,
  onTacticStatusChange,
  initiativeStatus,
}: MECETableRendererProps) {
  const dispatch = useAppDispatch()
  const { navigateToTab } = useTabNavigation()

  // ✅ CRITICAL: Hooks MUST be called before any conditional returns
  // Use pillar.name as the unique key since backend doesn't provide pillar_id
  const [expandedPillars, setExpandedPillars] = useState<Set<string>>(
    new Set((data?.pillars || []).map((_p, idx) => `pillar-${idx}`))
  )

  // Phase 3: Evidence traceability - track which tactics have expanded evidence
  const [expandedEvidence, setExpandedEvidence] = useState<Set<string>>(new Set())

  // ✅ FIX: Reset expanded state when data changes (conversation switch)
  useEffect(() => {
    if (data?.pillars) {
      setExpandedPillars(new Set(data.pillars.map((_p, idx) => `pillar-${idx}`)))
      setExpandedEvidence(new Set())
    }
  }, [data])

  // Phase 4.5: Quick Ask modal state
  const [quickAskContext, setQuickAskContext] = useState<QuickAskTacticContext | null>(null)
  const [isQuickAskLoading, setIsQuickAskLoading] = useState(false)

  // Now safe to do early returns after all hooks are called
  if (!data || !data.pillars || data.pillars.length === 0) {
    return (
      <div className="mece-table-renderer">
        <p className="no-data-message">No MECE table data available.</p>
      </div>
    )
  }

  const togglePillar = (pillarKey: string) => {
    const newExpanded = new Set(expandedPillars)
    if (newExpanded.has(pillarKey)) {
      newExpanded.delete(pillarKey)
    } else {
      newExpanded.add(pillarKey)
    }
    setExpandedPillars(newExpanded)
  }

  // Phase 3: Toggle evidence visibility for a tactic
  const toggleEvidence = (tacticKey: string) => {
    const newExpanded = new Set(expandedEvidence)
    if (newExpanded.has(tacticKey)) {
      newExpanded.delete(tacticKey)
    } else {
      newExpanded.add(tacticKey)
    }
    setExpandedEvidence(newExpanded)
  }

  // Phase 3: Handle node click to navigate to Business Graph and highlight node
  const handleNodeClick = (nodeId: string) => {
    // Navigate to Business Graph tab AND set the selected node
    navigateToGraphNode(nodeId, navigateToTab, dispatch)
    onNodeClick?.(nodeId)
  }

  // Phase 3: Get supporting node IDs from tactic (handles multiple field names)
  const getSupportingNodes = (tactic: any): string[] => {
    const nodes = tactic.supporting_node_ids ||
                  tactic.node_ids ||
                  tactic.citations ||
                  []
    // Phase 5: Filter out invalid node IDs
    return Array.isArray(nodes) ? nodes.filter((n: any) => typeof n === 'string' && n.length > 0) : []
  }

  // Phase 5: Safe text display with fallback
  const safeText = (text: any, fallback: string = 'No description available'): string => {
    if (typeof text === 'string' && text.trim().length > 0) {
      return text.trim()
    }
    return fallback
  }

  // Phase 4.5: Open Quick Ask modal for a tactic
  const openQuickAsk = (tactic: any, pillarName: string, tacticKey: string) => {
    setQuickAskContext({
      tacticId: tacticKey,
      tacticTitle: tactic.title,
      tacticDescription: tactic.description,
      pillarName: pillarName,
      supportingEvidence: getSupportingNodes(tactic)
    })
  }

  // Phase 4.5: Close Quick Ask modal
  const closeQuickAsk = () => {
    setQuickAskContext(null)
    setIsQuickAskLoading(false)
  }

  // Phase 4.5: Handle Quick Ask submission (async - keeps modal open during processing)
  const handleQuickAskSubmit = async (prompt: string, questionText: string, questionType: QuickAskQuestionType) => {
    if (!quickAskContext) return

    setIsQuickAskLoading(true)

    try {
      // Call the parent callback and wait for completion
      // This keeps the modal open with "Getting answer..." during the API call (up to 45s)
      if (onQuickAsk) {
        await onQuickAsk(prompt, questionText, questionType, quickAskContext)
      }
    } catch (error) {
      console.error('[MECETableRenderer] Quick Ask error:', error)
    } finally {
      // Close modal after API call completes (success or error)
      closeQuickAsk()
    }
  }

  return (
    <div className="mece-table-renderer">
      {/* Phase 4.5: Quick Ask Modal */}
      {quickAskContext && (
        <QuickAskModal
          isOpen={true}
          onClose={closeQuickAsk}
          tacticId={quickAskContext.tacticId}
          tacticTitle={quickAskContext.tacticTitle}
          tacticDescription={quickAskContext.tacticDescription}
          pillarName={quickAskContext.pillarName}
          supportingEvidence={quickAskContext.supportingEvidence}
          onSubmitQuestion={handleQuickAskSubmit}
          isLoading={isQuickAskLoading}
        />
      )}

      <div className="mece-grid">
        {data.pillars.map((pillar, pillarIdx) => {
          const pillarKey = `pillar-${pillarIdx}`
          const isSelected = selectedPillars?.has(pillar.name) ?? true
          return (
            <div key={pillarKey} className={`mece-pillar ${isSelectionMode && !isSelected ? 'unselected' : ''}`}>
              {/* Phase 1.1: Selection checkbox */}
              {isSelectionMode && (
                <div className="pillar-selection-checkbox">
                  <input
                    type="checkbox"
                    id={`select-${pillarKey}`}
                    checked={isSelected}
                    onChange={() => onPillarSelectionToggle?.(pillar.name)}
                    aria-label={`Include ${pillar.name} in strategy`}
                  />
                  <label htmlFor={`select-${pillarKey}`}>
                    Include in Strategy Map
                  </label>
                </div>
              )}
              <div className="pillar-header" onClick={() => togglePillar(pillarKey)}>
                <h5>
                  <span className="pillar-number">{getPillarNumber(pillar, pillarIdx)}</span>
                  {pillar.name}
                </h5>
                <span className="expand-icon">
                  {expandedPillars.has(pillarKey) ? '▼' : '▶'}
                </span>
              </div>

              {expandedPillars.has(pillarKey) && (
                <>
                  <p className="pillar-description">{safeText(pillar.description, 'No pillar description available')}</p>

                  {/* KPI Metrics for this pillar (deduplicated across tactics) */}
                  {(() => {
                    const seen = new Set<string>()
                    const pillarMetrics = (pillar.tactics || []).flatMap(
                      (t: any) => (t.metrics || [])
                    ).filter((m: any) => {
                      if (seen.has(m.id)) return false
                      seen.add(m.id)
                      return true
                    })
                    return pillarMetrics.length > 0 ? (
                      <MetricBadgeList metrics={pillarMetrics} compact={false} />
                    ) : null
                  })()}

                  {/* KSA Competencies for this pillar (deduplicated across tactics) */}
                  {(() => {
                    const seen = new Set<string>()
                    const pillarKSAs = (pillar.tactics || []).flatMap(
                      (t: any) => (t.ksas || [])
                    ).filter((k: any) => {
                      if (seen.has(k.id)) return false
                      seen.add(k.id)
                      return true
                    })
                    return pillarKSAs.length > 0 ? (
                      <KSABadgeList ksas={pillarKSAs} compact={false} />
                    ) : null
                  })()}

                  {/* Reference Documents for this pillar (deduplicated across tactics) */}
                  {(() => {
                    const seen = new Set<string>()
                    const pillarRefs = (pillar.tactics || []).flatMap(
                      (t: any) => (t.ref_docs || [])
                    ).filter((r: any) => {
                      if (seen.has(r.id)) return false
                      seen.add(r.id)
                      return true
                    })
                    return pillarRefs.length > 0 ? (
                      <RefDocBadgeList refDocs={pillarRefs} compact={false} />
                    ) : null
                  })()}

                  <div className="tactics-list">
                    {(!pillar.tactics || pillar.tactics.length === 0) ? (
                      <p className="no-tactics-message">No tactics defined for this pillar.</p>
                    ) : isReorderMode && onTacticReorder ? (
                      /* Phase 3.1: Use SortableTacticList in reorder mode */
                      <SortableTacticList
                        tactics={pillar.tactics}
                        pillarName={pillar.name}
                        pillarIndex={pillarIdx}
                        pillar={pillar}
                        isReorderMode={true}
                        onReorder={onTacticReorder}
                        onQuickAsk={(tactic, pName, tIdx) => openQuickAsk(tactic, pName, `${pillarKey}-tactic-${tIdx}`)}
                        onViewEvidence={(tactic) => {
                          const nodes = getSupportingNodes(tactic)
                          if (nodes.length > 0) {
                            handleNodeClick(nodes[0])
                          }
                        }}
                        onStatusChange={onTacticStatusChange}
                      />
                    ) : (
                      /* Standard tactic rendering */
                      pillar.tactics.map((tactic, tacticIdx) => {
                        const tacticKey = `${pillarKey}-tactic-${tacticIdx}`
                        const supportingNodes = getSupportingNodes(tactic)
                        const hasEvidence = supportingNodes.length > 0

                        // Jan 2026: Generate DOM ID for cross-navigation from Strategy Map
                        // Format: mece-tactic-P{pillarRank}T{tacticPriority}
                        const pillarRank = pillar.priority_rank ?? (pillarIdx + 1)
                        const tacticPriority = tactic.priority ?? (tacticIdx + 1)
                        const tacticDomId = `mece-tactic-P${pillarRank}T${tacticPriority}`

                        return (
                          <div key={tacticKey} id={tacticDomId} className="tactic-item">
                            <div className="tactic-header">
                              <h6>
                                <span className="tactic-number">{getTacticNumber(pillar, pillarIdx, tactic, tacticIdx)}</span>
                                {safeText(tactic.title, 'Untitled Tactic')}
                                {initiativeStatus?.[tactic.title] && (
                                  <span className={`tactic-status-pill status-${initiativeStatus[tactic.title]}`}>
                                    {initiativeStatus[tactic.title].replace('_', ' ')}
                                  </span>
                                )}
                              </h6>
                              <div className="tactic-actions">
                                {/* Phase 6.5: Tactic Inclusion Toggle for Scenario Mode */}
                                {isScenarioMode && activeScenario && (
                                  <TacticInclusionToggle
                                    value={getTacticInclusion(activeScenario, pillarIdx, tacticIdx)}
                                    onChange={(state) => onTacticInclusionChange?.(pillarIdx, tacticIdx, state)}
                                    size="small"
                                  />
                                )}
                                <button
                                  className={`btn-view-evidence ${expandedEvidence.has(tacticKey) ? 'active' : ''} ${!hasEvidence ? 'no-evidence' : ''}`}
                                  onClick={() => hasEvidence && toggleEvidence(tacticKey)}
                                  title={hasEvidence ? `View ${supportingNodes.length} supporting node(s)` : 'No supporting evidence available'}
                                  aria-expanded={hasEvidence ? expandedEvidence.has(tacticKey) : undefined}
                                  disabled={!hasEvidence}
                                >
                                  {expandedEvidence.has(tacticKey) ? 'Hide Evidence' : 'View Evidence'}
                                  <span className="evidence-count" aria-label={`${supportingNodes.length} nodes`}>{supportingNodes.length}</span>
                                </button>
                                {/* Phase 4.5: Quick Ask button */}
                                <button
                                  className="btn-quick-ask"
                                  onClick={() => openQuickAsk(tactic, pillar.name, tacticKey)}
                                  title="Ask a follow-up question about this recommendation"
                                  aria-label={`Ask question about ${safeText(tactic.title, 'this tactic')}`}
                                >
                                  ? Quick Ask
                                </button>
                              </div>
                            </div>
                            <p className="tactic-description">{safeText(tactic.description)}</p>
                            {tactic.rationale && (
                              <p className="tactic-rationale"><em>Rationale: {tactic.rationale}</em></p>
                            )}

                            {/* Phase 3: Expandable evidence panel */}
                            {hasEvidence && expandedEvidence.has(tacticKey) && (
                              <div className="evidence-panel">
                                <div className="evidence-header">
                                  <span className="evidence-icon">🔗</span>
                                  <span>Supporting Evidence from Business Graph</span>
                                </div>
                                <ul className="evidence-list">
                                  {supportingNodes.map((nodeId: string, nodeIdx: number) => (
                                    <li key={nodeIdx}>
                                      <button
                                        className="node-link"
                                        onClick={() => handleNodeClick(nodeId)}
                                        title="Click to highlight in Business Graph"
                                      >
                                        {nodeId}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                                <p className="evidence-hint">
                                  Click a node to highlight it in the Business Graph tab
                                </p>
                              </div>
                            )}
                          </div>
                        )
                      })
                    )}
                  </div>
                </>
              )}
              {/* Phase 5: Pillar stats */}
              {pillar.tactics && pillar.tactics.length > 0 && (
                <div className="pillar-stats">
                  <span className="stat-item">{pillar.tactics.length} tactic{pillar.tactics.length !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
